import { apiPost } from '../client';
import { LONG_TIMEOUT_MS } from '../../config/env';
import type { ResponseBase } from '../types/common';
import type {
  ChangeRequest,
  EditOptionAndPriceRequest,
  EditRequest,
  NoteRequest,
  OpenRequest,
  PostSaleResponse,
  RefundCancelRequest,
  RefundRequest,
  TicketImportRequest,
} from '../types/postSale';

/**
 * Post-sale reservation operations (legacy `ShopCart/*`, ported into the API in backend
 * Faz A). The shared `ReservationDetailPage` wires these split by record state (see
 * components/reservation/capabilities.ts): `editOptionAndPrice` for a still-offered
 * reservation, `refundReservation` (here) + `voidShoppingCart` (../shoppingCart.ts) for
 * an issued ticket.
 *
 * SA-3 (2026-09-07): the Open / Change / Edit / AddNote / RefundCancel wrappers are back
 * (shapes from `Docs/api/04-commerce-payments.md`), but the UI renders every button
 * DISABLED — the backend `OpenOrChange` / `Edit` skeleton mutates the cart + Arkman
 * ticketing DB with no business-rule guard (docs/backend-requirements.md §N-1). The
 * `arkman/start-ticket-import` wrapper (`startTicketImport`) is genuinely live.
 */

export function editOptionAndPrice(body: EditOptionAndPriceRequest): Promise<PostSaleResponse> {
  return apiPost<EditOptionAndPriceRequest, PostSaleResponse>('ShoppingCart/EditOptionAndPrice', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/** `ShoppingCart/Refund` — refund an issued ticket. Business-rule enforced on the backend
 *  (see `RefundRequest` doc); returns the mutated cart + a `successMessage`. */
export function refundReservation(body: RefundRequest): Promise<PostSaleResponse> {
  return apiPost<RefundRequest, PostSaleResponse>('ShoppingCart/Refund', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/* ---------------------------------------------------------------------------
 * SA-3 — destructive `ShoppingCart/*` post-sale ops. BACKEND SKELETON: no business-rule
 * guard on the server side. The disabled "Bilet İşlemleri" UI card was removed
 * (2026-09-08); these wrappers are kept so the feature can be rebuilt when the backend
 * lands the real logic (see docs/backend-requirements.md §N-1).
 * ------------------------------------------------------------------------- */

/** `ShoppingCart/Open` — mark a ticket "on request / open". */
export function openShoppingCart(body: OpenRequest): Promise<PostSaleResponse> {
  return apiPost<OpenRequest, PostSaleResponse>('ShoppingCart/Open', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/** `ShoppingCart/Change` — ticket exchange / reissue. */
export function changeShoppingCart(body: ChangeRequest): Promise<PostSaleResponse> {
  return apiPost<ChangeRequest, PostSaleResponse>('ShoppingCart/Change', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/** `ShoppingCart/Edit` — amend fee / service-fee / POS of an existing post-sale process. */
export function editShoppingCartProcess(body: EditRequest): Promise<PostSaleResponse> {
  return apiPost<EditRequest, PostSaleResponse>('ShoppingCart/Edit', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/** `ShoppingCart/AddNote` — append a free-text line to `ShoppingCart.Notes`. Comparatively
 *  safe; still shipped disabled by default — flip it on once the backend confirms. */
export function addShoppingCartNote(body: NoteRequest): Promise<PostSaleResponse> {
  return apiPost<NoteRequest, PostSaleResponse>('ShoppingCart/AddNote', body);
}

/** `ShoppingCart/RefundCancel` — reverse a previously recorded refund by `processId`. */
export function refundCancel(body: RefundCancelRequest): Promise<PostSaleResponse> {
  return apiPost<RefundCancelRequest, PostSaleResponse>('ShoppingCart/RefundCancel', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/* ---------------------------------------------------------------------------
 * SA-3 — Arkman ticket import. Swagger-hidden route; envelope-only response. Rendered as
 * a per-ticket action at the bottom of every air ticket-detail page
 * (components/reservation/TicketImportSection.tsx).
 * ------------------------------------------------------------------------- */

export function startTicketImport(body: TicketImportRequest): Promise<ResponseBase> {
  return apiPost<TicketImportRequest, ResponseBase>('arkman/start-ticket-import', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}
